import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/roles";
import { AppShell } from "@/components/app/AppShell";
import { Input } from "@/components/ui/input";

const CAT_LABELS = { promotional: "Promotional", marketing: "Mixed marketing", account_updates: "Account updates" };
const selectCls = "h-10 rounded-md border border-gray-200 bg-white px-3 text-sm text-oath-ink";

const UnsubscribeButton = ({ row, onDone }) => {
  const [confirm, setConfirm] = useState(false);
  if (row.unsubscribed_at) {
    return <span className="font-mono-label text-[9px] text-slate-500" data-testid={`optin-unsubscribed-${row.id}`}>UNSUBSCRIBED {fmtDate(row.unsubscribed_at)}</span>;
  }
  const run = async () => {
    try {
      await api.post(`/admin/opt-ins/${row.id}/unsubscribe`);
      toast.success(`${row.email} unsubscribed`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return confirm ? (
    <span className="flex gap-1">
      <button onClick={run} className="rounded-full bg-red-600 px-3 py-1 text-xs text-white" data-testid={`optin-unsubscribe-confirm-${row.id}`}>Confirm</button>
      <button onClick={() => setConfirm(false)} className="rounded-full px-2 py-1 text-xs text-oath-muted">Cancel</button>
    </span>
  ) : (
    <button onClick={() => setConfirm(true)} className="rounded-full border border-gray-300 px-3 py-1 text-xs text-oath-ink hover:bg-gray-50" data-testid={`optin-unsubscribe-${row.id}`}>
      Mark unsubscribed
    </button>
  );
};

export default function AdminOptIns() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState(null);
  const [exporting, setExporting] = useState(false);

  const params = useCallback(() => new URLSearchParams({ q, category, status }).toString(), [q, category, status]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/opt-ins?${params()}`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  }, [params]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/opt-ins/export?${params()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `residenceproof-opt-ins-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const actions = (
    <button onClick={exportCsv} disabled={exporting} data-testid="optins-export-button"
      className="inline-flex items-center gap-2 rounded-full bg-oath-ink px-5 py-2.5 font-mono-label text-[11px] font-semibold text-white hover:bg-black disabled:opacity-60">
      {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} EXPORT CSV
    </button>
  );

  return (
    <AppShell eyebrow="— SUPER ADMIN" title="Communication opt-ins" actions={actions}>
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-oath-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone" className="h-10 bg-white pl-9" data-testid="optins-search-input" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls} data-testid="optins-category-filter">
          <option value="">All categories</option>
          {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls} data-testid="optins-status-filter">
          <option value="">All statuses</option>
          <option value="active">Subscribed</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
      </div>
      <div className="mt-5 overflow-x-auto rounded-2xl border border-gray-200 bg-white">
        <table className="w-full min-w-[860px] text-left text-sm" data-testid="optins-table">
          <thead className="border-b border-gray-200 bg-oath-bg/60 font-mono-label text-[9px] text-oath-muted">
            <tr>
              <th className="px-4 py-3">NAME</th><th className="px-4 py-3">CONTACT</th><th className="px-4 py-3">CATEGORIES</th>
              <th className="px-4 py-3">SMS MARKETING</th><th className="px-4 py-3">2FA TEXTS</th><th className="px-4 py-3">CONSENTED</th><th className="px-4 py-3">IP</th><th className="px-4 py-3">STATUS</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} className="px-4 py-8"><Loader2 className="h-5 w-5 animate-spin text-oath-muted" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-oath-muted" data-testid="optins-empty">No opt-ins match.</td></tr>}
            {rows?.map((r) => (
              <tr key={r.id} className="border-b border-gray-100 last:border-0" data-testid={`optin-row-${r.id}`}>
                <td className="px-4 py-3 font-medium text-oath-ink">{r.full_name}</td>
                <td className="px-4 py-3 text-oath-secondary"><div>{r.email}</div><div className="font-mono text-xs">{r.phone}</div></td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {r.categories.map((c) => <span key={c} className="rounded-full bg-oath-accent-light px-2 py-0.5 text-[11px] text-oath-accent">{CAT_LABELS[c] || c}</span>)}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono-label text-[9px]" data-testid={`optin-sms-${r.id}`}>{r.sms_marketing_consent ? <span className="text-emerald-700">YES</span> : <span className="text-slate-500">NO</span>}</td>
                <td className="px-4 py-3 font-mono-label text-[9px]" data-testid={`optin-2fa-${r.id}`}>{r.twofa_consent === true ? <span className="text-emerald-700">YES</span> : r.twofa_consent === false ? <span className="text-slate-500">DECLINED</span> : <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 font-mono text-xs text-oath-secondary">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3 font-mono text-xs text-oath-secondary">{r.ip}</td>
                <td className="px-4 py-3"><UnsubscribeButton row={r} onDone={load} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
